import React, { useState, useEffect } from 'react'

import { useModal } from '@/contexts/ModalContext'

import { ENDPOINTS, sendPostRequest } from '@/services/api'
import { onUncaughtError } from '@/services/userAlerts'

import { useTranslation } from 'react-i18next'
import { i18n } from '@/translate/i18n'
import Swal from 'sweetalert2'

import * as S from './styles'

export interface CreateFolderCollectionModalConfig {
  action: boolean
  setAction: (action: boolean) => void
  setLoading: (loading: boolean) => void
  currentFolder?: string
  isIntercompany?: boolean
}

export interface CreateFolderCollectionModalProps {
  config: CreateFolderCollectionModalConfig
}

const CreateFolderCollectionModal = ({
  config
}: CreateFolderCollectionModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useModal()

  const [createFolderText, setCreateFolderText] = useState<string>('')
  const [createFolderPlaceholder, setCreateFolderPlaceholder] =
    useState<string>('')
  const [cancelText, setCancelText] = useState<string>()
  const [createFolderLabelText, setCreateFolderLabelText] = useState<string>()

  const [folderName, setFolderName] = useState('')

  useEffect(() => {
    setCreateFolderText(t('buttons.createFolder'))
    setCreateFolderPlaceholder(t('placeholders.createFolder'))
    setCancelText(`${t('buttons.cancel')}`)
    setCreateFolderLabelText(`${t('labels.folderName')}`)
  }, [t])

  const onSuccess = () => {
    config.setAction(!config.action)
    Swal.fire({
      icon: 'success',
      title: i18n.t('swal.success'),
      html: i18n.t('messages.collectionFolderCreated')
    })
  }

  const createFolder = async e => {
    e.preventDefault()
    if (config.isIntercompany) {
      Swal.fire({
        icon: 'info',
        title: i18n.t('swal.attention'),
        html: i18n.t('messages.lockedFolderNoCreate')
      })
      return
    }
    config.setLoading(true)
    closeModal()
    const body = { name: folderName, folder_id: config.currentFolder }

    try {
      await sendPostRequest(ENDPOINTS.create_folder, body)
      onSuccess()
    } catch (error) {
      const msg = error.data || error
      console.log(msg)
      onUncaughtError()
    } finally {
      config.setLoading(false)
    }
  }

  return (
    config && (
      <S.CreateFolderCollectionModal>
        <S.CreateFolderCollectionTitle>
          <p>{createFolderText}</p>
          <button
            id="btn_closemodal_createfolder"
            type="button"
            onClick={closeModal}
          >
            <span className="material-icons">close</span>
          </button>
        </S.CreateFolderCollectionTitle>

        <form onSubmit={createFolder}>
          <label htmlFor={createFolderText}>{createFolderLabelText}</label>
          <input
            type="text"
            placeholder={createFolderPlaceholder}
            name={createFolderText}
            onChange={e => setFolderName(e.target.value)}
            maxLength={50}
            id="inp_create_folder"
            required
            disabled={!!config.isIntercompany}
            title={
              config.isIntercompany
                ? i18n.t('messages.lockedFolderNoCreate')
                : undefined
            }
          />
          <div>
            <button
              type="button"
              id="btn_cancel_create_folder"
              onClick={closeModal}
            >
              {cancelText}
            </button>
            {!config.isIntercompany && (
              <button
                type="submit"
                disabled={!!config.isIntercompany}
                title={
                  config.isIntercompany
                    ? i18n.t('messages.lockedFolderNoCreate')
                    : undefined
                }
                id="btn_create_folder"
              >
                {createFolderText}
              </button>
            )}
          </div>
        </form>
      </S.CreateFolderCollectionModal>
    )
  )
}

export default CreateFolderCollectionModal
